/**
 * The host half of the seam.
 *
 * A package describes a page; everything it needs to *be* a page — the gift
 * fetch, the category filter, the countdown tick, the dialogs, the lightbox —
 * lives here, so no template can express a business rule or hold state.
 *
 * This is the component `EventPublicPage` renders instead of the TSX switch when
 * an event's template is an installed package.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { PublicRsvpForm } from "@/components/public/PublicRsvpForm";
import { PublicGiftContributionForm } from "@/components/public/PublicGiftContributionForm";
import { PublicLightbox } from "@/components/public/PublicLightbox";
import { getPublicEventGifts, type PublicEvent, type PublicGiftItem } from "@/services/api/publicEvents";
import type { HostSurfaceConfig } from "@/lib/event-templates";
import { buildGoogleCalendarUrl, downloadIcsFile } from "@/utils/saveTheDate";
import { TemplateRenderer } from "./TemplateRenderer";
import { stringsForLocale } from "./loadPackage";
import { paletteConfigFor } from "./resolve";
import { fontScaleFor } from "./tokens";
import { buildCeremonies, computeCountdown } from "./context";
import type { EngineContext, TemplatePackage } from "./types";

export type TemplatePageHostProps = {
  pkg: TemplatePackage;
  event: PublicEvent & { hasEnabledGifts?: boolean };
  locale: string;
  /**
   * Colours for the host-owned dialogs and lightbox, so they sit inside the
   * template's colours. Derived from the package itself when not given.
   */
  config?: HostSurfaceConfig;
  /** The owner's signed preview token, so the registry opens on an unpublished page too. */
  previewToken?: string | null;
};

/** True when the visitor has asked for less motion. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined"
      && typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

/**
 * A clock that only ticks while a countdown is actually running.
 *
 * Once the date has passed the countdown collection is empty and nothing on the
 * page depends on the second, so the interval stops rather than re-rendering the
 * whole template forever. Under reduced motion it ticks once a minute.
 */
function useCountdownClock(date: string | null | undefined, prefersReducedMotion: boolean): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!date || computeCountdown(date, new Date()) === null) return;

    const interval = prefersReducedMotion ? 60_000 : 1_000;
    const timer = setInterval(() => {
      const next = new Date();
      setNow(next);
      // Stop once the date passes: the countdown collection is empty from then
      // on, so ticking would re-render the whole page for nothing.
      if (computeCountdown(date, next) === null) clearInterval(timer);
    }, interval);

    return () => clearInterval(timer);
  }, [date, prefersReducedMotion]);

  return now;
}

export function TemplatePageHost({ pkg, event, locale, config, previewToken }: TemplatePageHostProps) {
  const { t } = useTranslation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const now = useCountdownClock(event.date, prefersReducedMotion);

  /* ------------------------------------------------------------- gifts */

  const [gifts, setGifts] = useState<PublicGiftItem[]>([]);
  const [categories, setCategories] = useState<Array<{ id: string | null; name: string }>>([]);
  const [cardPaymentsEnabled, setCardPaymentsEnabled] = useState<boolean | undefined>(undefined);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  const [giftsLoading, setGiftsLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");

  const registryAllowed = Boolean(event.registryPublic) && Boolean(event.giftsActive);
  const identifier = (event.url || event.uuid).replace(/^@/, "");

  const loadGifts = useCallback(async () => {
    const response = await getPublicEventGifts(identifier, undefined, previewToken);
    setGifts(Array.isArray(response?.gifts) ? response.gifts : []);
    setCategories(Array.isArray(response?.categories) ? response.categories : []);
    setCardPaymentsEnabled(response?.cardPaymentsEnabled);
    setStripeAccountId(response?.stripeAccountId ?? null);
  }, [identifier, previewToken]);

  useEffect(() => {
    if (!registryAllowed) {
      setGifts([]);
      setCategories([]);
      return;
    }

    let cancelled = false;
    setGiftsLoading(true);
    loadGifts()
      .catch(() => {
        if (cancelled) return;
        setGifts([]);
        setCategories([]);
      })
      .finally(() => {
        if (!cancelled) setGiftsLoading(false);
      });

    return () => { cancelled = true; };
  }, [registryAllowed, loadGifts]);

  /* ----------------------------------------------------------- dialogs */

  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [selectedGift, setSelectedGift] = useState<PublicGiftItem | null>(null);
  const [contributionOpen, setContributionOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  /* ----------------------------------------------------------- actions */

  const share = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: event.name, url: window.location.href });
        return;
      } catch {
        return; // the visitor dismissed the share sheet
      }
    }
    await navigator.clipboard.writeText(window.location.href);
    toast.success(t("common.copied"));
  }, [event.name, t]);

  const scrollTo = useCallback((anchor: string) => {
    document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  /* ----------------------------------------------------------- context */

  const ctx: EngineContext = useMemo(() => ({
    event: { ...event, hasEnabledGifts: event.hasEnabledGifts ?? gifts.length > 0 },
    locale,
    t,
    now,
    prefersReducedMotion,
    fontScale: fontScaleFor(event.fontScale),
    gifts: { items: gifts, categories, activeCategory, cardPaymentsEnabled, loading: giftsLoading },
    strings: stringsForLocale(pkg, locale),
    actions: {
      rsvpOpen: () => setRsvpOpen(true),
      giftContribute: (gift) => { setSelectedGift(gift); setContributionOpen(true); },
      lightboxOpen: (index) => setLightboxIndex(index),
      filterSet: setActiveCategory,
      share,
      calendarGoogle: () => {
        const url = buildGoogleCalendarUrl(event, locale);
        if (url) window.open(url, "_blank", "noopener,noreferrer");
      },
      calendarIcs: () => { downloadIcsFile(event, locale); },
      scrollTo,
      mapOpen: (url) => { if (url) window.open(url, "_blank", "noopener,noreferrer"); },
    },
  }), [event, locale, t, now, prefersReducedMotion, gifts, categories, activeCategory,
       cardPaymentsEnabled, giftsLoading, pkg, share, scrollTo]);

  /* ------------------------------------------------------------- slots */

  const rsvpEnabled = Boolean(event.rsvpSettings?.rsvpEnabled);
  // Never an empty stand-in: the RSVP dialog mounts with the page, and a config
  // missing the fields it reads used to blank the whole invitation (JUB-161).
  const paletteConfig = useMemo(
    () => config ?? paletteConfigFor(pkg, event.colorScheme),
    [config, pkg, event.colorScheme],
  );

  const slots = {
    rsvpDialog: rsvpEnabled ? (
      <PublicRsvpForm
        event={event}
        config={paletteConfig}
        locale={locale}
        open={rsvpOpen}
        onOpenChange={setRsvpOpen}
      />
    ) : null,

    giftContributionDialog: selectedGift ? (
      <PublicGiftContributionForm
        event={event}
        gift={selectedGift}
        config={paletteConfig}
        locale={locale}
        cardPaymentsEnabled={cardPaymentsEnabled}
        stripeAccountId={stripeAccountId}
        open={contributionOpen}
        onOpenChange={(open) => {
          setContributionOpen(open);
          if (!open) setSelectedGift(null);
        }}
        onSuccess={() => { void loadGifts().catch(() => undefined); }}
      />
    ) : null,

    lightbox: (
      <PublicLightbox
        images={event.gallery ?? []}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        scrim={paletteConfig.palette.text ? `${paletteConfig.palette.text}f2` : undefined}
        surface={paletteConfig.palette.background}
        textColor={paletteConfig.palette.text}
      />
    ),
  };

  return <TemplateRenderer pkg={pkg} ctx={ctx} slots={slots} />;
}

/** Ceremony list, re-exported so the page can reason about a package the same way. */
export { buildCeremonies };
